import React from "react";
import { X, ChevronLeft, Check, CheckIcon } from "lucide-react";
import { motion } from "framer-motion";
import DividerComponent from "../../ui/DividerComponent";
import { Checkbox } from "radix-ui";
import EnhancedInput from "../../ui/EnhancedInput";

// Componente para formulario de login
export const LoginForm = ({
  signInData,
  handleChange,
  handleSignIn,
  loading,
  showPassword,
  setShowPassword,
  setIsRegistering,
}) => {
  return (
    <form onSubmit={handleSignIn} className="space-y-4 px-6 pb-6 pt-4">
      <EnhancedInput
        type="email"
        placeholder="Correo electrónico"
        value={signInData.email}
        onChange={(e) => handleChange("email", e.target.value)}
        required
      />

      <EnhancedInput
        type="password"
        placeholder="Contraseña"
        value={signInData.password}
        onChange={(e) => handleChange("password", e.target.value)}
        showPassword={showPassword}
        togglePassword={() => setShowPassword((v) => !v)}
        required
      />

      <button
        type="submit"
        className="w-full bg-blue-600 text-white py-3 rounded-2xl text-lg"
        disabled={loading}
      >
        {loading ? "Cargando..." : "Iniciar Sesión"}
      </button>

      <div className="text-sm text-center ">
        ¿No tienes cuenta?{" "}
        <a
          className="text-blue-600 underline mt-4 cursor-pointer"
          href="/registro"
        >
          Crear una cuenta
        </a>
      </div>
    </form>
  );
};

// Componente para la cabecera de registro
export const RegistrationHeader = ({ setIsRegistering }) => (
  <div className="flex items-center gap-2 px-4 py-4 border-b">
    <button onClick={() => setIsRegistering(false)}>
      <ChevronLeft size={24} className="text-gray-600" />
    </button>
    <h2 className="text-lg font-medium text-gray-800">Registro en Fluenti</h2>
  </div>
);

// Componente para sección de información personal
export const PersonalInfoSection = ({
  signUpData,
  handleChange,
  fieldStatus,
  phonePrefix,
  setPhonePrefix,
  formDisabled,
  validationErrors,
}) => (
  <div>
    <h3 className="text-md font-medium text-gray-600 mb-4">
      Información personal
    </h3>

    <EnhancedInput
      type="text"
      placeholder="Nombres"
      value={signUpData.first_name}
      onChange={(e) => handleChange("first_name", e.target.value)}
      className="mb-3"
      disabled={formDisabled}
      required
    />

    <EnhancedInput
      type="text"
      placeholder="Apellidos"
      value={signUpData.last_name}
      onChange={(e) => handleChange("last_name", e.target.value)}
      className="mb-3"
      disabled={!fieldStatus.last_name || formDisabled}
      required
    />

    <EnhancedInput
      type="email"
      placeholder="Correo electrónico"
      value={signUpData.email}
      onChange={(e) => handleChange("email", e.target.value)}
      className="mb-3"
      disabled={!fieldStatus.email || formDisabled}
      required
    />

    <div className="flex mb-3">
      <select
        value={phonePrefix}
        onChange={(e) => setPhonePrefix(e.target.value)}
        className={`bg-gray-50 shadow-sm ring-1 ring-gray-300 px-2 py-3 rounded-l-2xl ${
          !fieldStatus.phone || formDisabled
            ? "opacity-60 cursor-not-allowed bg-gray-100"
            : ""
        }`}
        disabled={!fieldStatus.phone || formDisabled}
      >
        <option value="+57">+57</option>
        <option value="+1">+1</option>
        <option value="+34">+34</option>
        <option value="+52">+52</option>
        <option value="+54">+54</option>
        <option value="+56">+56</option>
        <option value="+51">+51</option>
        <option value="+58">+58</option>
        <option value="+593">+593</option>
      </select>

      <EnhancedInput
        type="tel"
        inputMode="numeric"
        placeholder="Número de teléfono"
        value={signUpData.phone}
        onChange={(e) => {
          // Guardamos tanto el valor formateado como el valor numérico puro
          handleChange("phone", e.target.value);
          // Si necesitas acceder al valor sin formato, puedes guardarlo en otro campo
          if (e.target.numericValue) {
            handleChange("phone_raw", e.target.numericValue);
          }
        }}
        className="rounded-l-none"
        disabled={!fieldStatus.phone || formDisabled}
        errorMessage={validationErrors?.phone}
        required
      />
    </div>
    <p className="text-xs text-gray-500 -mt-2 mb-3">
      En lo posible, que cuente con WhatsApp
    </p>
  </div>
);

// Componente para sección de identificación
export const IdentificationSection = ({
  signUpData,
  handleChange,
  fieldStatus,
  documentTypes,
  countries,
  formDisabled,
  validationErrors,
}) => (
  <div>
    <h3 className="text-md font-medium text-gray-600 mb-4">Identificación</h3>

    <select
      value={signUpData.nationality}
      onChange={(e) => handleChange("nationality", e.target.value)}
      className={`bg-gray-50 w-full shadow-sm ring-1 ring-gray-300 px-4 py-3 rounded-2xl mb-3 ${
        !fieldStatus.nationality
          ? "opacity-60 cursor-not-allowed bg-gray-100"
          : ""
      }`}
      disabled={!fieldStatus.nationality}
      required
    >
      <option value="">Selecciona tu nacionalidad</option>
      {countries.map((c) => (
        <option key={c} value={c}>
          {c.charAt(0).toUpperCase() + c.slice(1)}
        </option>
      ))}
    </select>

    <select
      value={signUpData.document_type}
      onChange={(e) => handleChange("document_type", e.target.value)}
      className={`bg-gray-50 w-full shadow-sm ring-1 ring-gray-300 px-4 py-3 rounded-2xl mb-3 ${
        !fieldStatus.document_type
          ? "opacity-60 cursor-not-allowed bg-gray-100"
          : ""
      }`}
      disabled={!fieldStatus.document_type}
      required
    >
      <option value="">Tipo de documento</option>
      {documentTypes.map((doc) => (
        <option key={doc.value} value={doc.value}>
          {doc.label}
        </option>
      ))}
    </select>

    <EnhancedInput
      type="number"
      placeholder="Número de documento"
      value={signUpData.document_value}
      onChange={(e) => {
        // Para el documento, queremos mostrar el valor formateado pero guardar el numérico
        handleChange("document_value", e.target.value);
        // Si hay un valor numérico puro disponible, lo guardamos en un campo separado
        if (e.target.numericValue) {
          handleChange("document_value_raw", e.target.numericValue);
        }
      }}
      disabled={!fieldStatus.document_value || formDisabled}
      errorMessage={validationErrors?.document_value}
      required
    />
  </div>
);

// Componente para sección de contraseña
export const PasswordSection = ({
  signUpData,
  handleChange,
  fieldStatus,
  showPassword,
  setShowPassword,
  validations,
  passwordError,
}) => (
  <div>
    <h3 className="text-md font-medium text-gray-600 mb-4">Contraseña</h3>

    <EnhancedInput
      type="password"
      placeholder="Contraseña"
      value={signUpData.password}
      onChange={(e) => handleChange("password", e.target.value)}
      disabled={!fieldStatus.password}
      showPassword={showPassword}
      togglePassword={() => setShowPassword((v) => !v)}
      validations={{
        minLength: {
          isValid: validations.minLength,
          text: "Al menos 8 caracteres",
        },
        hasUpperCase: {
          isValid: validations.hasUpperCase,
          text: "Al menos una mayúscula",
        },
        hasNumber: {
          isValid: validations.hasNumber,
          text: "Al menos un número",
        },
      }}
      errorMessage={passwordError}
      required
    />
  </div>
);

// Componente para términos y condiciones
export const TermsAndConditionsSection = ({
  acceptTerms,
  setAcceptTerms,
  fieldStatus,
}) => (
  <div>
    <h3 className="text-md font-medium text-gray-600 mb-4">
      Términos y condiciones
    </h3>
    <div
      className={`flex flex-row-reverse justify-between items-center gap-2 text-sm text-gray-600 mt-4 ${
        !fieldStatus.password ? "opacity-60" : ""
      }`}
    >
      <Checkbox.Root
        className="flex h-6 w-6 items-center justify-center rounded border-2 border-gray-800 bg-white hover:bg-gray-50"
        checked={acceptTerms}
        onCheckedChange={() => setAcceptTerms((v) => !v)}
        id="terms-checkbox"
        disabled={!fieldStatus.password}
      >
        <Checkbox.Indicator>
          <CheckIcon size={16} className="text-blue-600" />
        </Checkbox.Indicator>
      </Checkbox.Root>
      <label
        htmlFor="terms-checkbox"
        className={`cursor-pointer ${
          !fieldStatus.password ? "opacity-60" : ""
        }`}
      >
        Acepto los{" "}
        <a href="/terminos" target="_blank" className="text-blue-600 underline">
          términos y condiciones
        </a>{" "}
        de uso
      </label>
    </div>
  </div>
);

// Componente de formulario de registro completo
export const RegistrationForm = ({
  signUpData,
  handleChange,
  handleSignUp,
  fieldStatus,
  showPassword,
  setShowPassword,
  validations,
  passwordError,
  phonePrefix,
  setPhonePrefix,
  acceptTerms,
  setAcceptTerms,
  formProgress,
  loading,
  documentTypes,
  countries,
  formDisabled,
  validationErrors,
  isModal,
}) => (
  <>
    <div
      className={`flex-1 px-6 pb-[1200px] max-h-[667px] ${
        formDisabled ? "opacity-60 pointer-events-none" : ""
      }`}
    >
      <form onSubmit={handleSignUp} className="space-y-4 pt-4 ">
        <div className="text-sm text-gray-600 bg-green-50 border  rounded-xl px-4 py-3 mt-2 flex flex-col gap-2">
          <span>♻️ Tu registro hace los botellones reusables</span>
          <span>🌎 Ayudas a recuperar plástico</span>
          <span>

          💧 Cada pedido evita residuos. ¡Gracias!
          </span>
        </div>

        <PersonalInfoSection
          signUpData={signUpData}
          handleChange={handleChange}
          fieldStatus={fieldStatus}
          phonePrefix={phonePrefix}
          setPhonePrefix={setPhonePrefix}
          formDisabled={formDisabled}
          validationErrors={validationErrors}
        />

        <DividerComponent margin={24} color="eeeeee" size={1} />

        <IdentificationSection
          signUpData={signUpData}
          handleChange={handleChange}
          fieldStatus={fieldStatus}
          documentTypes={documentTypes}
          countries={countries}
          formDisabled={formDisabled}
          validationErrors={validationErrors}
        />

        <DividerComponent margin={24} color="eeeeee" size={1} />

        <PasswordSection
          signUpData={signUpData}
          handleChange={handleChange}
          fieldStatus={fieldStatus}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          validations={validations}
          passwordError={passwordError}
          formDisabled={formDisabled}
        />

        <DividerComponent margin={24} color="eeeeee" size={1} />

        <TermsAndConditionsSection
          acceptTerms={acceptTerms}
          setAcceptTerms={setAcceptTerms}
          fieldStatus={fieldStatus}
          formDisabled={formDisabled}
        />
      </form>
      {!isModal && (
        <div className="py-16 w-full flex justify-center">
          <button
            onClick={handleSignUp}
            disabled={
              loading ||
              formProgress < 100 ||
              !Object.values(validations).every((val) => val) ||
              formDisabled
            }
            className={` ${
              formProgress === 100 &&
              Object.values(validations).every((val) => val) &&
              !loading &&
              !formDisabled
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-400 cursor-not-allowed"
            } text-white py-3 rounded-2xl text-xl transition-colors fraunces px-4 font-bold`}
          >
            {loading ? "Registrando..." : "Crear Cuenta"}
          </button>
          
        </div>
      )}
    </div>
    {isModal && (
      <div className="fixed bottom-0 right-0 bg-white border-t min-w-[100%]">
        <div
          className={`"w-full bg-gray-200 h-1 rounded-full ${
            formProgress < 100 ? "" : "hidden"
          }`}
        >
          <div
            className="bg-blue-600 h-1 rounded-full "
            style={{ width: `${formProgress}%` }}
          ></div>
        </div>
        <div className="px-6 py-4">
          <button
            onClick={handleSignUp}
            disabled={
              loading ||
              formProgress < 100 ||
              !Object.values(validations).every((val) => val) ||
              formDisabled
            }
            className={`w-full ${
              formProgress === 100 &&
              Object.values(validations).every((val) => val) &&
              !loading &&
              !formDisabled
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-blue-400 cursor-not-allowed"
            } text-white py-3 rounded-2xl text-lg transition-colors`}
          >
            {loading ? "Registrando..." : "Crear Cuenta"}
          </button>
        </div>
      </div>
    )}
  </>
);

// Componente para la vista de registro exitoso
export const RegistrationSuccess = ({
  email,
  setIsRegistering,
  emailProvider,
}) => (
  <div className="p-8 flex flex-col items-center">
    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5">
      <Check size={36} className="text-green-600" />
    </div>
    <h2 className="text-2xl font-medium text-gray-800 mb-3 text-center">
      ¡Registro Exitoso!
    </h2>
    <p className="text-gray-600 text-center mb-8">
      Hemos enviado un correo a <span className="font-semibold">{email}</span>{" "}
      para confirmar tu cuenta. Por favor revisa tu bandeja de entrada.
    </p>

    {emailProvider && (
      <a
        href={emailProvider.url}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full bg-blue-100 hover:bg-blue-200 transition-colors text-blue-600 py-3 rounded-2xl text-center mb-4 font-medium"
      >
        Abrir {emailProvider.name}
      </a>
    )}

    <button
      onClick={setIsRegistering}
      className="w-full bg-blue-600 hover:bg-blue-700 transition-colors text-white py-3 rounded-2xl font-medium"
    >
      Volver al inicio de sesión
    </button>
  </div>
);

// Componente para el modal completo
export const ModalContainer = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center px-4">
    <motion.div
      className="bg-white rounded-2xl overflow-hidden shadow-xl w-full max-w-md max-h-[95vh] flex flex-col relative"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-gray-500 hover:text-black z-10"
      >
        <X size={20} />
      </button>
      {children}
    </motion.div>
  </div>
);

// Componente para la cabecera de inicio de sesión
export const LoginHeader = () => (
  <div className="text-center pt-6 pb-2 px-6">
    <img
      src="/assets/logos/fluenti-logo-black-140.svg"
      alt="logo"
      className="h-14 mx-auto mb-2"
    />
    <p className="text-gray-600 text-base font-['Fredoka']">
      Ingresa para continuar tu pedido
    </p>
  </div>
);

// Componente para mostrar errores
export const ErrorMessage = ({ error }) => (
  <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm text-center mx-6 mb-4">
    {error}
  </div>
);
